import { AppHeaderAction } from '@/components/AppHeader/AppHeaderAction';
import { useStoreState } from '@/stores/store';
import { useCallback, useLayoutEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import api from '../../api/axios';
import { useNoInternetDialog } from '@/hooks/useNoInternetDialog';
import { useTextsetContext } from '@/contexts/TextSetContext/useTextsetContext';
import { SecondHeader } from '@/components/SecondHeader';
import { Icon } from '@/components/Icon';
import { logError } from '@/utils/logHandling';
import { fetchUpdatedPatientWithRetry } from '@/utils/fetchUpdatedPatientWithRetry';
import { SetSaveModal } from '@/components/SetsSelectedData/SetSaveModal';
import { Modal } from '@/components/ui/therapist/Modal';
import { Button } from '@/components/ui/therapist/Button';
import { Text } from '@/components/Text';
import { replacedPatientProfileHeader } from '@/utils/stringHelpers';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import { StackScreenProps } from '@react-navigation/stack';
import { TherapistStackParamList } from '@/navigations/TherapistStack';

type HeaderProps = {
  props: StackScreenProps<TherapistStackParamList, 'TextSet'>;
};

type InputState = { name: string; appDisplayName: string };

const styles = StyleSheet.create({
  header: {
    flexDirection: 'column',
    zIndex: 1,
  },
});

export function Header({ props }: HeaderProps) {
  const [isDemoTherapistVisible, setIsDemoTherapistVisible] = useState(false);
  const { isTherapistDemo } = useStoreState((store) => store);
  const { checkInternet } = useNoInternetDialog();
  const { showNotification } = useNotification();
  const {
    apiKeyName,
    setId,
    editData,
    patientId,
    dynamicName,
    selectedData,
    dataKeyName,
    term,
    termType,
    changeOuterState,
    tags,
  } = useTextsetContext();
  const [isVisible, setIsVisible] = useState(false);
  const noTextsSelected = !selectedData.length;

  const [inputState, setInputState] = useState<InputState>({
    name: '',
    appDisplayName: '',
  });

  const handleButtonPress = useCallback(() => {
    if (isTherapistDemo) {
      setIsDemoTherapistVisible(true);
    } else {
      setIsVisible((prevState) => {
        if (setId && editData != null) {
          setInputState({
            name: editData.name,
            appDisplayName: editData.appDisplayName,
          });
        }
        return !prevState;
      });
    }
  }, [setId, editData]);

  const handleInputChange = useCallback((key: string) => {
    return (value: string) => {
      setInputState((prevState) => {
        return {
          ...prevState,
          [key]: value,
        };
      });
    };
  }, []);

  const handleModalAcceptButton = async () => {
    try {
      await checkInternet();

      if (setId) {
        const state = { ...inputState };
        if (state.name == '') {
          state.name = 'Neues Texteset';
        }
        if (state.appDisplayName == '') {
          state.appDisplayName = 'Texte';
        }

        const bodyData = {
          ...editData,
          [`${dynamicName}Items`]: [],
          ...state,
          updatedAt: new Date().toISOString(),
          patientId: patientId,
          [dataKeyName]: selectedData,
        };
        await api.put(
          `/api/patients/${patientId}/${apiKeyName}/${setId}?api_version=v2`,
          bodyData,
        );

        if (patientId) {
          const { patients } = await fetchUpdatedPatientWithRetry(
            patientId,
            'textsets',
            setId,
          );

          showNotification({
            type: 'success',
            title: 'Set erfolgreich gespeichert.',
          });

          props.navigation.navigate('PatientProfile', {
            id: patientId,
            patients,
            replacedTitle: replacedPatientProfileHeader({
              patients,
              patientId,
              patient: undefined,
            }),
          });
        }
      } else {
        const state = { ...inputState };
        if (state.name == '') {
          state.name = 'Neues Texteset';
        }
        if (state.appDisplayName == '') {
          state.appDisplayName = 'Texte';
        }

        const bodyData = {
          ...state,
          patientId: patientId,
          [dataKeyName]: selectedData,
        };
        const { data } = await api.post(
          `/api/patients/${patientId}/${apiKeyName}?api_version=v2`,
          bodyData,
        );

        if (patientId) {
          const { patients } = await fetchUpdatedPatientWithRetry(
            patientId,
            'textsets',
            data._id,
          );

          showNotification({
            type: 'success',
            title: 'Set erfolgreich gespeichert.',
          });

          props.navigation.navigate('PatientProfile', {
            id: patientId,
            patients,
            replacedTitle: replacedPatientProfileHeader({
              patients,
              patientId,
              patient: undefined,
            }),
          });
        }
      }
    } catch (error) {
      logError('Could not update set', error);
    }
  };

  useLayoutEffect(() => {
    props.navigation.setOptions({
      headerRight: () => (
        <View style={{ gap: 30, flexDirection: 'row' }}>
          <AppHeaderAction
            primary
            onPress={() => props.navigation.goBack()}
            icon={<Icon name="close" scale={1.2} />}
            text="Abbrechen"
          />
          <AppHeaderAction
            primary
            disabled={noTextsSelected}
            onPress={handleButtonPress}
            icon={<Icon name="save" />}
            text="Speichern"
          />
        </View>
      ),
    });
  }, [noTextsSelected, handleButtonPress]);

  return (
    <View style={styles.header}>
      <Modal
        isVisible={isDemoTherapistVisible}
        title="In der Demo nicht möglich"
        confirmActionButton={
          <Button
            label="OK"
            look="primary"
            onPress={() => setIsDemoTherapistVisible(false)}
          />
        }
        onClose={() => setIsDemoTherapistVisible(false)}
      >
        <Text style={{ fontSize: 18 }}>
          Registrieren Sie sich bei neolexon, um Ihre eigenen Patient:innen und
          Übungssets zu erstellen.
        </Text>
      </Modal>

      <SecondHeader
        term={term}
        termType={termType}
        changeOuterState={
          changeOuterState as (key: string, value: unknown) => void
        }
        tags={tags}
        type="text"
      />
      <SetSaveModal
        isVisible={isVisible}
        setIsVisible={setIsVisible}
        handleInputChange={handleInputChange}
        handleModalAcceptButton={handleModalAcceptButton}
        inputState={inputState}
        handleButtonPress={handleButtonPress}
        modalTitle="Texteset speichern"
        apiKeyName={apiKeyName}
      />
    </View>
  );
}
